<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const props = defineProps({
  isViewOnly: {
    type: Boolean,
    default: false,
  },
});
const router = useRouter();
const route = useRoute();
const store = useStore();
const { t: $t } = useI18n();

const isViewMode = computed(
  () => props.isViewOnly || route.name === 'campaigns_whatsapp_view'
);

const isEditMode = ref(false);
const editingCampaignId = ref(null);

const campaignName = ref('');
const selectedWhatsappAccount = ref('');
const isLoadingContacts = ref(false);
const isSaving = ref(false);
const selectedContactIds = ref([]);
const hoveredContactId = ref(null);
const searchQuery = ref('');
const selectedLabels = ref([]);
const isLabelDropdownOpen = ref(false);
const selectedCustomAttributeFilters = ref([]);
const customAttributeDraft = ref({
  attributeKey: '',
  operator: 'equals',
  value: '',
});
const hasAudienceSelection = computed(() => {
  return (
    selectedContactIds.value.length > 0 ||
    selectedLabels.value.length > 0 ||
    selectedCustomAttributeFilters.value.length > 0
  );
});
const currentPage = ref(1);
const allFilteredContacts = ref([]);
const isFilteredByAudience = ref(false);

const activeTab = ref(0);
const selectedTemplate = ref('');
const templateParserRef = ref(null);
const templateParserKey = ref(0);
const savedTemplateData = ref(null);
const isSyncingTemplates = ref(false);
const stepsFinished = ref([false, false, false]);
const sendDate = ref('');
const sendHour = ref('09');
const sendMinute = ref('00');
const sendTimezone = ref('America/Sao_Paulo');

const allLabels = useMapGetter('labels/getLabels');
const contactCustomAttributes = useMapGetter('attributes/getContactAttributes');
const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const CUSTOM_ATTRIBUTE_TYPES_MAP = {
  0: 'text',
  1: 'number',
  2: 'currency',
  3: 'percent',
  4: 'link',
  5: 'date',
  6: 'list',
  7: 'checkbox',
};

function resolveCustomAttributeType(type) {
  if (typeof type === 'string') {
    return type;
  }

  return CUSTOM_ATTRIBUTE_TYPES_MAP[type] || 'text';
}

function buildCustomAttributeAudience() {
  return selectedCustomAttributeFilters.value.map(filter => {
    if (filter.operator === 'not_equals') {
      return {
        type: 'Custom_attribute',
        name: filter.name,
        '!value': filter.value,
      };
    }

    return {
      type: 'Custom_attribute',
      name: filter.name,
      value: filter.value,
    };
  });
}

function buildLabelAudience() {
  return selectedLabels.value.map(labelId => ({
    type: 'Label',
    id: labelId,
  }));
}

function buildAudienceEntries() {
  const selectedContactsAudience = selectedContactIds.value.map(contactId => ({
    type: 'Contact',
    id: contactId,
  }));

  return [
    ...selectedContactsAudience,
    ...buildLabelAudience(),
    ...buildCustomAttributeAudience(),
  ];
}

const whatsappAccounts = computed(() => {
  const inboxes = whatsappInboxes.value || [];
  return inboxes.map(inbox => ({
    value: String(inbox.id),
    label: `${inbox.name} | ${inbox.phone_number || ''}`,
  }));
});

const templateOptions = computed(() => {
  if (!selectedWhatsappAccount.value) return [];

  const inboxId = Number(selectedWhatsappAccount.value);
  const templates = getFilteredWhatsAppTemplates.value(inboxId);

  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const currentTemplate = computed(() => {
  if (!selectedTemplate.value) return null;
  return templateOptions.value.find(
    option => option.value === selectedTemplate.value
  )?.template;
});

const syncTemplates = async () => {
  if (!selectedWhatsappAccount.value) {
    useAlert($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.ACCOUNT_REQUIRED'));
    return;
  }

  isSyncingTemplates.value = true;
  try {
    await store.dispatch(
      'inboxes/syncTemplates',
      Number(selectedWhatsappAccount.value)
    );
    useAlert($t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS'));
  } catch (error) {
    useAlert($t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSyncingTemplates.value = false;
  }
};

const onSendNow = async () => {
  const errors = [];

  if (!campaignName.value || campaignName.value.trim() === '') {
    errors.push(
      $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.TITLE_REQUIRED')
    );
  }

  if (!selectedWhatsappAccount.value) {
    errors.push(
      $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.ACCOUNT_REQUIRED')
    );
  }

  if (!hasAudienceSelection.value) {
    errors.push(
      $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.CONTACTS_REQUIRED')
    );
  }

  if (!selectedTemplate.value) {
    errors.push(
      $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.TEMPLATE_REQUIRED')
    );
  }

  if (!sendDate.value) {
    errors.push($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.DATE_REQUIRED'));
  }

  if (!sendHour.value || !sendMinute.value) {
    errors.push($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.TIME_REQUIRED'));
  }

  if (sendDate.value && sendHour.value && sendMinute.value) {
    const scheduledDateTime = new Date(
      `${sendDate.value}T${sendHour.value}:${sendMinute.value}:00`
    );
    const now = new Date();
    const minDateTime = new Date(now.getTime() + 60000);

    if (scheduledDateTime <= minDateTime) {
      errors.push(
        $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.FUTURE_TIME_REQUIRED')
      );
    }
  }

  if (errors.length > 0) {
    const errorMessage =
      $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.VALIDATION_TITLE') +
      '\n\n' +
      errors.map((err, idx) => `${idx + 1}. ${err}`).join('\n');
    useAlert(errorMessage);
    return;
  }

  isSaving.value = true;
  try {
    const audience = buildAudienceEntries();

    const dateTimeString = `${sendDate.value}T${sendHour.value}:${sendMinute.value}:00`;
    const scheduledAt = new Date(dateTimeString);

    const parserData = savedTemplateData.value;

    if (!parserData || !parserData.renderedTemplate) {
      useAlert(
        $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.TEMPLATE_DATA_MISSING')
      );
      return;
    }

    const templateContent = parserData.renderedTemplate;

    const templateParams = {
      name: currentTemplate.value?.name || '',
      namespace: currentTemplate.value?.namespace || '',
      category: currentTemplate.value?.category || 'UTILITY',
      language: currentTemplate.value?.language || 'en_US',
      processed_params: parserData.processedParams || {},
    };

    const campaignData = {
      campaign: {
        title: campaignName.value,
        inbox_id: Number(selectedWhatsappAccount.value),
        message: templateContent,
        campaign_type: 'one_off',
        enabled: true,
        campaign_status: 'active',
        scheduled_at: scheduledAt.toISOString(),
        audience: audience,
        template_params: templateParams,
      },
    };

    if (isEditMode.value && editingCampaignId.value) {
      await store.dispatch('campaigns/update', {
        id: editingCampaignId.value,
        ...campaignData.campaign,
      });
      useAlert(
        $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.CAMPAIGN_UPDATED')
      );
    } else {
      await store.dispatch('campaigns/create', campaignData);
      useAlert(
        $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.CAMPAIGN_CREATED')
      );
    }

    router.push({ name: 'campaigns_whatsapp_index' });
  } catch (error) {
    useAlert($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.ERROR_CREATE'));
  } finally {
    isSaving.value = false;
  }
};

const handleBack = () => {
  router.push({ name: 'campaigns_whatsapp_index' });
};

const tabs = [
  { label: $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.TABS.CONTACTS') },
  { label: $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.TABS.CONTENT') },
  { label: $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.TABS.SEND') },
];

const itemsPerPage = computed(() => {
  return store.getters['contacts/getMeta']?.perPage || 15;
});

const contactsList = computed(() => {
  return store.getters['contacts/getContacts'] || [];
});

const visibleContacts = computed(() => {
  return contactsList.value;
});

const labelOptions = computed(() => {
  const labels = allLabels.value || [];
  return labels.map(label => ({
    value: label.id,
    label: label.title,
    color: label.color,
  }));
});

const customAttributeOptions = computed(() => {
  const attributes = contactCustomAttributes.value || [];
  return attributes.map(attribute => ({
    key: attribute.attributeKey,
    label: attribute.attributeDisplayName,
    type: resolveCustomAttributeType(attribute.attributeDisplayType),
    values: attribute.attributeValues || [],
  }));
});

const selectedCustomAttributeOption = computed(() => {
  return customAttributeOptions.value.find(
    option => option.key === customAttributeDraft.value.attributeKey
  );
});

const selectedCustomAttributeInputType = computed(() => {
  return selectedCustomAttributeOption.value?.type || 'text';
});

const customAttributeValueOptions = computed(() => {
  if (selectedCustomAttributeInputType.value === 'checkbox') {
    return [
      {
        value: 'true',
        label: $t(
          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.TRUE'
        ),
      },
      {
        value: 'false',
        label: $t(
          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.FALSE'
        ),
      },
    ];
  }

  return (selectedCustomAttributeOption.value?.values || []).map(value => ({
    value,
    label: value,
  }));
});

const hasCustomAttributeDraftValue = computed(() => {
  const value = customAttributeDraft.value.value;
  return value !== null && value !== undefined && `${value}`.trim() !== '';
});

const getCustomAttributeOperatorLabel = operator => {
  if (operator === 'not_equals') {
    return $t(
      'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.OPERATOR_NOT_EQUALS'
    );
  }

  return $t(
    'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.OPERATOR_EQUALS'
  );
};

const onCustomAttributeKeyChange = () => {
  customAttributeDraft.value.value = '';
  customAttributeDraft.value.operator = 'equals';
};

const addCustomAttributeFilter = () => {
  if (
    !customAttributeDraft.value.attributeKey ||
    !hasCustomAttributeDraftValue.value
  ) {
    return;
  }

  const currentAttribute = customAttributeOptions.value.find(
    attribute => attribute.key === customAttributeDraft.value.attributeKey
  );
  if (!currentAttribute) {
    return;
  }

  selectedCustomAttributeFilters.value.push({
    type: 'Custom_attribute',
    name: customAttributeDraft.value.attributeKey,
    operator: customAttributeDraft.value.operator,
    value: `${customAttributeDraft.value.value}`,
    label: currentAttribute.label,
    inputType: currentAttribute.type,
  });

  customAttributeDraft.value.value = '';
};

const removeCustomAttributeFilter = index => {
  selectedCustomAttributeFilters.value.splice(index, 1);
};

const toggleLabel = labelId => {
  const index = selectedLabels.value.indexOf(labelId);
  if (index > -1) {
    selectedLabels.value.splice(index, 1);
  } else {
    selectedLabels.value.push(labelId);
  }
};

const isLabelSelected = labelId => {
  return selectedLabels.value.includes(labelId);
};

const selectedLabelsText = computed(() => {
  if (selectedLabels.value.length === 0) {
    return $t(
      'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.FILTER_BY_LABELS'
    );
  }
  if (selectedLabels.value.length === 1) {
    const label = labelOptions.value.find(
      l => l.value === selectedLabels.value[0]
    );
    return label?.label || '';
  }
  return `${selectedLabels.value.length} ${$t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.LABELS_SELECTED_PLURAL')}`;
});

const totalContactsCount = computed(() => {
  return store.getters['contacts/getMeta']?.count || 0;
});

const totalPages = computed(() => {
  const count = totalContactsCount.value;
  const perPage = itemsPerPage.value;
  return Math.ceil(count / perPage) || 1;
});

const areAllVisibleContactsSelected = computed(() => {
  if (visibleContacts.value.length === 0) return false;
  return visibleContacts.value.every(contact =>
    selectedContactIds.value.includes(contact.id)
  );
});

const areSomeVisibleContactsSelected = computed(() => {
  if (visibleContacts.value.length === 0) return false;
  const visibleContactIds = visibleContacts.value.map(c => c.id);
  const selectedVisible = selectedContactIds.value.filter(id =>
    visibleContactIds.includes(id)
  );
  return (
    selectedVisible.length > 0 &&
    selectedVisible.length < visibleContacts.value.length
  );
});

const selectContact = contactId => {
  if (!selectedContactIds.value.includes(contactId)) {
    selectedContactIds.value.push(contactId);
  }
};

const deselectContact = contactId => {
  selectedContactIds.value = selectedContactIds.value.filter(
    id => id !== contactId
  );
};

const isContactSelected = contactId => {
  return selectedContactIds.value.includes(contactId);
};

const selectAllContacts = checked => {
  if (checked) {
    const visibleContactIds = visibleContacts.value.map(contact => contact.id);
    const newSelection = new Set([
      ...selectedContactIds.value,
      ...visibleContactIds,
    ]);
    selectedContactIds.value = Array.from(newSelection);
  } else {
    const visibleContactIds = visibleContacts.value.map(contact => contact.id);
    selectedContactIds.value = selectedContactIds.value.filter(
      id => !visibleContactIds.includes(id)
    );
  }
};

const onContactCheckboxChange = (contactId, checked) => {
  if (checked) {
    selectContact(contactId);
  } else {
    deselectContact(contactId);
  }
};

const isTemplateFormValid = computed(() => {
  if (!selectedTemplate.value) return false;
  if (!templateParserRef.value) return true;
  return !templateParserRef.value.v$?.$invalid;
});

const templateInitialParams = computed(() => {
  return savedTemplateData.value?.processedParams;
});

function onCancel() {
  router.push({ name: 'campaigns_whatsapp_index' });
}

async function onSaveExit() {
  if (!campaignName.value || !selectedWhatsappAccount.value) {
    useAlert(
      $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.SAVE_DRAFT_REQUIRED')
    );
    return;
  }

  if (
    activeTab.value === 1 &&
    templateParserRef.value &&
    currentTemplate.value
  ) {
    savedTemplateData.value = {
      processedParams: JSON.parse(
        JSON.stringify(templateParserRef.value.processedParams)
      ),
      renderedTemplate: templateParserRef.value.renderedTemplate,
    };
  }

  isSaving.value = true;
  try {
    const audience = buildAudienceEntries();

    let scheduledAt = null;
    if (sendDate.value && sendHour.value && sendMinute.value) {
      const dateTimeString = `${sendDate.value}T${sendHour.value}:${sendMinute.value}:00`;
      scheduledAt = new Date(dateTimeString).toISOString();
    }

    let templateContent = selectedTemplate.value || 'Rascunho';
    let templateParams = {};

    if (selectedTemplate.value && currentTemplate.value) {
      const parserData = templateParserRef.value || savedTemplateData.value;
      templateContent = parserData?.renderedTemplate || templateContent;

      templateParams = {
        name: currentTemplate.value?.name || '',
        namespace: currentTemplate.value?.namespace || '',
        category: currentTemplate.value?.category || 'UTILITY',
        language: currentTemplate.value?.language || 'en_US',
        processed_params: parserData?.processedParams || {},
      };
    }

    const campaignData = {
      campaign: {
        title: campaignName.value,
        inbox_id: Number(selectedWhatsappAccount.value),
        message: templateContent,
        campaign_type: 'one_off',
        enabled: false,
        campaign_status: 'draft',
        audience: audience,
        template_params: templateParams,
      },
    };

    if (scheduledAt) {
      campaignData.campaign.scheduled_at = scheduledAt;
    }

    if (isEditMode.value && editingCampaignId.value) {
      await store.dispatch('campaigns/update', {
        id: editingCampaignId.value,
        ...campaignData.campaign,
      });
      useAlert($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.DRAFT_UPDATED'));
    } else {
      await store.dispatch('campaigns/create', campaignData);
      useAlert($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.DRAFT_CREATED'));
    }

    router.push({ name: 'campaigns_whatsapp_index' });
  } catch (error) {
    useAlert($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.ERROR_SAVE'));
  } finally {
    isSaving.value = false;
  }
}

function onContinue() {
  if (activeTab.value === 0 && hasAudienceSelection.value) {
    stepsFinished.value[0] = true;
    activeTab.value = 1;
  } else if (activeTab.value === 1 && selectedTemplate.value) {
    if (templateParserRef.value && currentTemplate.value) {
      savedTemplateData.value = {
        processedParams: JSON.parse(
          JSON.stringify(templateParserRef.value.processedParams)
        ),
        renderedTemplate: templateParserRef.value.renderedTemplate,
      };
    }
    stepsFinished.value[1] = true;
    activeTab.value = 2;
  } else if (activeTab.value === 2) {
    stepsFinished.value[2] = true;
  }
}

const isStepCompleted = index => stepsFinished.value[index];
const isStepEnabled = index => {
  if (index === 0) return true;
  return stepsFinished.value[index - 1];
};

const fetchContactsAndLabels = async (page = 1) => {
  isLoadingContacts.value = true;
  currentPage.value = page;
  try {
    await Promise.all([
      store.dispatch('contacts/get', { page, sortAttr: 'name' }),
      store.dispatch('labels/get'),
    ]);

    const contacts = store.getters['contacts/getContacts'] || [];
    const labelPromises = contacts.map(contact =>
      store.dispatch('contactLabels/get', contact.id)
    );
    await Promise.all(labelPromises);
  } catch (error) {
    // Error loading contacts
  } finally {
    isLoadingContacts.value = false;
  }
};

const buildContactsFilterPayload = () => {
  const payload = [];

  if (selectedLabels.value.length > 0) {
    const labelTitles = selectedLabels.value
      .map(id => allLabels.value.find(l => l.id === id)?.title)
      .filter(Boolean);

    if (labelTitles.length > 0) {
      payload.push({
        attribute_key: 'labels',
        filter_operator: 'equal_to',
        values: labelTitles,
        query_operator: 'and',
        attribute_model: 'standard',
        custom_attribute_type: '',
      });
    }
  }

  selectedCustomAttributeFilters.value.forEach(filter => {
    payload.push({
      attribute_key: filter.name,
      filter_operator:
        filter.operator === 'not_equals' ? 'not_equal_to' : 'equal_to',
      values: [filter.value],
      query_operator: 'and',
      attribute_model: 'contact_attribute',
      custom_attribute_type: 'contact_attribute',
    });
  });

  if (payload.length > 0) {
    payload[payload.length - 1].query_operator = undefined;
    return { payload };
  }

  return null;
};

const fetchAllContactsForAudienceFilter = async (page = 1) => {
  isLoadingContacts.value = true;
  isFilteredByAudience.value = true;
  currentPage.value = page;

  try {
    const queryPayload = buildContactsFilterPayload();

    if (!queryPayload) {
      isFilteredByAudience.value = false;
      await fetchContactsAndLabels(page);
      return;
    }

    await store.dispatch('contacts/filter', {
      page: page,
      sortAttr: 'name',
      queryPayload: queryPayload,
      resetState: true,
    });

    const contacts = store.getters['contacts/getContacts'] || [];
    const labelPromises = contacts.map(contact =>
      store.dispatch('contactLabels/get', contact.id)
    );
    await Promise.all(labelPromises);
  } catch (error) {
    // Error loading contacts
  } finally {
    isLoadingContacts.value = false;
  }
};

let searchTimeout = null;
const searchContacts = async query => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(async () => {
    isLoadingContacts.value = true;
    currentPage.value = 1;
    try {
      if (query && query.trim()) {
        await store.dispatch('contacts/search', {
          search: query.trim(),
          page: 1,
          sortAttr: 'name',
        });
      } else {
        await store.dispatch('contacts/get', { page: 1, sortAttr: 'name' });
      }

      const contacts = store.getters['contacts/getContacts'] || [];
      const labelPromises = contacts.map(contact =>
        store.dispatch('contactLabels/get', contact.id)
      );
      await Promise.all(labelPromises);
    } catch (error) {
      // Error searching contacts
    } finally {
      isLoadingContacts.value = false;
    }
  }, 300);
};

const changePage = newPage => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    if (isFilteredByAudience.value) {
      fetchAllContactsForAudienceFilter(newPage);
    } else {
      fetchContactsAndLabels(newPage);
    }
  }
};

const selectAllFromFilter = async () => {
  isLoadingContacts.value = true;
  try {
    const allContactIds = [];
    const meta = store.getters['contacts/getMeta'];
    const totalPagesBackend = Math.ceil(
      (meta?.count || 0) / (meta?.perPage || 15)
    );

    const selectAllPromises = [];
    for (let page = 1; page <= totalPagesBackend; page += 1) {
      selectAllPromises.push(
        (async () => {
          if (isFilteredByAudience.value) {
            const queryPayload = buildContactsFilterPayload();
            const payload = await store.dispatch('contacts/filter', {
              page,
              sortAttr: 'name',
              queryPayload,
              resetState: false,
            });
            return payload.map(c => c.id);
          }
          if (searchQuery.value && searchQuery.value.trim()) {
            await store.dispatch('contacts/search', {
              search: searchQuery.value.trim(),
              page,
              sortAttr: 'name',
            });
            const currentPageContacts =
              store.getters['contacts/getContacts'] || [];
            return currentPageContacts.map(c => c.id);
          }
          await store.dispatch('contacts/get', { page, sortAttr: 'name' });
          const currentPageContacts =
            store.getters['contacts/getContacts'] || [];
          return currentPageContacts.map(c => c.id);
        })()
      );
    }

    const allPageContactIds = await Promise.all(selectAllPromises);
    allPageContactIds.forEach(pageIds => {
      allContactIds.push(...pageIds);
    });

    const newSelection = new Set([
      ...selectedContactIds.value,
      ...allContactIds,
    ]);
    selectedContactIds.value = Array.from(newSelection);

    if (isFilteredByAudience.value) {
      await fetchAllContactsForAudienceFilter(currentPage.value);
    } else if (searchQuery.value && searchQuery.value.trim()) {
      await searchContacts(searchQuery.value);
    } else {
      await fetchContactsAndLabels(currentPage.value);
    }
  } catch (error) {
    // Error selecting all contacts
  } finally {
    isLoadingContacts.value = false;
  }
};

watch(searchQuery, newQuery => {
  searchContacts(newQuery);
});

const applyFilters = async () => {
  if (
    selectedLabels.value.length > 0 ||
    selectedCustomAttributeFilters.value.length > 0
  ) {
    await fetchAllContactsForAudienceFilter(1);
  } else {
    isFilteredByAudience.value = false;
    allFilteredContacts.value = [];
    await fetchContactsAndLabels(1);
  }
};
watch(
  () => selectedWhatsappAccount.value,
  () => {
    selectedTemplate.value = '';
    savedTemplateData.value = null;
  }
);

watch(
  () => selectedTemplate.value,
  (newVal, oldVal) => {
    if (isEditMode.value && !oldVal && newVal) {
      return;
    }
    savedTemplateData.value = null;
  }
);

const resetForm = () => {
  isEditMode.value = false;
  editingCampaignId.value = null;

  campaignName.value = '';
  selectedWhatsappAccount.value = '';
  selectedContactIds.value = [];
  selectedTemplate.value = '';
  savedTemplateData.value = null;
  templateParserKey.value = 0;
  sendDate.value = '';
  sendHour.value = '09';
  sendMinute.value = '00';

  activeTab.value = 0;
  stepsFinished.value = [false, false, false];

  searchQuery.value = '';
  selectedLabels.value = [];
  selectedCustomAttributeFilters.value = [];
  customAttributeDraft.value = {
    attributeKey: '',
    operator: 'equals',
    value: '',
  };
  currentPage.value = 1;
  isFilteredByAudience.value = false;
  allFilteredContacts.value = [];
};

const loadCampaignForEdit = async () => {
  if (!route.params.id) return;

  isEditMode.value = true;
  editingCampaignId.value = route.params.id;

  try {
    const response = await CampaignsAPI.show(route.params.id);
    const campaign = response.data;

    campaignName.value = campaign.title;

    if (campaign.audience && Array.isArray(campaign.audience)) {
      const contactIds = campaign.audience
        .filter(item => item.type === 'Contact' || item.type === 'contact')
        .map(item => item.id);
      selectedContactIds.value = contactIds;

      const labelIds = campaign.audience
        .filter(item => item.type === 'Label' || item.type === 'label')
        .map(item => item.id);
      selectedLabels.value = labelIds;

      selectedCustomAttributeFilters.value = campaign.audience
        .filter(item => {
          const type = item.type?.toString().toLowerCase();
          return type === 'custom_attribute';
        })
        .map(item => {
          const operator = Object.prototype.hasOwnProperty.call(item, '!value')
            ? 'not_equals'
            : 'equals';
          const value = Object.prototype.hasOwnProperty.call(item, '!value')
            ? item['!value']
            : item.value;
          const attribute = customAttributeOptions.value.find(
            option => option.key === item.name
          );

          return {
            type: 'Custom_attribute',
            name: item.name,
            operator,
            value: `${value ?? ''}`,
            label: attribute?.label || item.name,
            inputType: attribute?.type || 'text',
          };
        })
        .filter(filter => filter.name && filter.value !== '');
    }

    if (campaign.scheduled_at) {
      const scheduledDate = new Date(campaign.scheduled_at * 1000);

      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');

      sendDate.value = `${year}-${month}-${day}`;
      sendHour.value = scheduledDate.getHours().toString().padStart(2, '0');
      sendMinute.value = scheduledDate.getMinutes().toString().padStart(2, '0');
    }

    const inboxId = campaign.inbox_id || campaign.inbox?.id;

    await nextTick();

    let attempts = 0;
    const maxAttempts = 10;
    while (
      (!whatsappAccounts.value || whatsappAccounts.value.length === 0) &&
      attempts < maxAttempts
    ) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, 50);
      });
      attempts += 1;
    }

    selectedWhatsappAccount.value = inboxId ? String(inboxId) : '';

    await nextTick();
    if (!selectedWhatsappAccount.value && inboxId) {
      selectedWhatsappAccount.value = String(inboxId);
    }

    await nextTick();

    if (campaign.template_params?.name) {
      let templateAttempts = 0;
      const maxTemplateAttempts = 10;
      while (
        templateOptions.value.length === 0 &&
        templateAttempts < maxTemplateAttempts
      ) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          setTimeout(resolve, 50);
        });
        templateAttempts += 1;
      }

      const matchingTemplate = templateOptions.value.find(
        option => option.template.name === campaign.template_params.name
      );

      if (matchingTemplate) {
        selectedTemplate.value = matchingTemplate.value;

        if (campaign.template_params.processed_params) {
          savedTemplateData.value = {
            processedParams: campaign.template_params.processed_params,
            renderedTemplate: campaign.message,
          };

          await nextTick();
          await nextTick();
          await new Promise(resolve => {
            setTimeout(resolve, 100);
          });

          templateParserKey.value += 1;
        }
      }
    }
  } catch (error) {
    useAlert($t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.ALERTS.ERROR_LOAD'));
    router.push({ name: 'campaigns_whatsapp_index' });
  }
};

watch(
  () => [route.name, route.params.id],
  async ([newName, newId], [oldName, oldId]) => {
    if (newName !== oldName || newId !== oldId) {
      resetForm();

      await store.dispatch('inboxes/get');
      await store.dispatch('attributes/get');

      if (newId) {
        await loadCampaignForEdit();
      }

      await fetchContactsAndLabels(1);
    }
  },
  { immediate: false }
);

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await store.dispatch('attributes/get');

  if (route.params.id) {
    await loadCampaignForEdit();
  }

  fetchContactsAndLabels();
});
</script>

<template>
  <div
    class="flex flex-col h-full overflow-auto bg-slate-50 dark:bg-n-background"
  >
    <header
      class="bg-white dark:bg-n-solid-1 border-b border-slate-100 dark:border-n-weak px-2 py-4"
    >
      <div class="flex items-center">
        <button class="flex items-center text-slate-500" @click="handleBack">
          <svg
            class="w-5 h-5 mr-1"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span class="text-base font-medium">{{
            $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.HEADER.BACK')
          }}</span>
        </button>
        <h1
          class="text-2xl font-semibold ml-2 text-slate-900 dark:text-n-slate-12"
        >
          {{
            isViewMode
              ? $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.HEADER.TITLE_VIEW')
              : isEditMode
                ? $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.HEADER.TITLE_EDIT')
                : $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.HEADER.TITLE_CREATE')
          }}
        </h1>
      </div>
    </header>

    <main class="p-6">
      <div
        class="bg-white dark:bg-n-solid-1 rounded-xl border border-slate-200 dark:border-n-weak p-6 mb-8"
      >
        <div class="grid grid-cols-2 gap-6">
          <div>
            <Input
              v-model="campaignName"
              :label="
                $t(
                  'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.FORM.CAMPAIGN_NAME.LABEL'
                )
              "
              :placeholder="
                $t(
                  'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.FORM.CAMPAIGN_NAME.PLACEHOLDER'
                )
              "
              required
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-900 dark:text-n-slate-12 mb-1"
            >
              {{
                $t(
                  'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.FORM.WHATSAPP_ACCOUNT.LABEL'
                )
              }}
              <span class="text-n-ruby-9">*</span>
            </label>
            <ComboBox
              v-model="selectedWhatsappAccount"
              :options="whatsappAccounts"
              :placeholder="
                $t(
                  'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.FORM.WHATSAPP_ACCOUNT.PLACEHOLDER'
                )
              "
              required
            />
          </div>
        </div>
      </div>

      <div
        class="bg-white dark:bg-n-solid-1 rounded-xl border border-slate-200 dark:border-n-weak"
      >
        <div class="flex rounded-lg overflow-hidden h-14">
          <template v-for="(tab, index) in tabs" :key="tab.label">
            <button
              class="flex flex-1 min-w-0 items-center justify-start h-full focus:outline-none transition-colors duration-200 px-6"
              :class="[
                activeTab === index
                  ? 'bg-slate-50 dark:bg-n-solid-2'
                  : 'bg-white dark:bg-n-solid-1',
                index > 0 && !isStepEnabled(index)
                  ? 'cursor-not-allowed opacity-60'
                  : '',
              ]"
              type="button"
              :disabled="index > 0 && !isStepEnabled(index)"
              @click="
                (index === 0 || isStepEnabled(index)) && (activeTab = index)
              "
            >
              <span
                class="flex items-center justify-center w-7 h-7 rounded-full border mr-2 text-base"
                :class="
                  isStepCompleted(index)
                    ? 'bg-woot-500 border-woot-500 text-white'
                    : 'border-woot-500 text-woot-500 bg-white dark:bg-n-solid-1'
                "
              >
                <template v-if="isStepCompleted(index)">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="currentColor" />
                    <path
                      d="M6 10.5L9 13.5L14 8.5"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </template>
                <template v-else>
                  {{ index + 1 }}
                </template>
              </span>
              <span
                class="truncate font-bold"
                :class="
                  activeTab === index
                    ? 'text-woot-500'
                    : 'text-slate-700 dark:text-n-slate-12'
                "
              >
                {{ tab.label }}
              </span>
            </button>
          </template>
        </div>
        <div class="p-8">
          <div v-if="activeTab === 0">
            <div class="font-bold mb-1 text-slate-900 dark:text-n-slate-12">
              {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.TITLE') }}
            </div>
            <div class="text-slate-500 dark:text-n-slate-11 mb-6">
              {{
                $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.SUBTITLE')
              }}
            </div>

            <div
              v-if="selectedContactIds.length > 0"
              class="bg-woot-25 dark:bg-woot-800/10 border border-woot-75 dark:border-woot-700/30 rounded-lg p-4 mb-4 flex items-center justify-between"
            >
              <label class="flex items-center gap-3">
                <input
                  type="checkbox"
                  class="w-4 h-4 cursor-pointer"
                  :checked="areAllVisibleContactsSelected"
                  :indeterminate.prop="areSomeVisibleContactsSelected"
                  @change="selectAllContacts($event.target.checked)"
                />
                <span class="font-medium text-slate-700 dark:text-n-slate-12">
                  {{ selectedContactIds.length }}
                  {{
                    selectedContactIds.length === 1
                      ? $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.SELECTED_COUNT_SINGULAR'
                        )
                      : $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.SELECTED_COUNT_PLURAL'
                        )
                  }}
                </span>
              </label>
              <button
                class="text-sm text-woot-500 hover:text-woot-600 font-medium"
                @click="selectedContactIds = []"
              >
                {{
                  $t(
                    'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CLEAR_SELECTION'
                  )
                }}
              </button>
            </div>

            <div
              class="mb-4 border border-slate-200 dark:border-n-weak rounded-lg bg-slate-25 dark:bg-n-solid-2 p-4"
            >
              <div class="flex gap-4 items-start">
                <div class="flex-1">
                  <div class="relative">
                    <input
                      v-model="searchQuery"
                      type="text"
                      :placeholder="
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.SEARCH_PLACEHOLDER'
                        )
                      "
                      class="w-full h-[42px] px-4 py-2 pr-10 border border-slate-200 dark:border-n-weak rounded-lg bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 placeholder:text-slate-400 dark:placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    />
                    <button
                      v-if="searchQuery"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-n-slate-10 hover:text-slate-600 dark:hover:text-n-slate-12"
                      @click="searchQuery = ''"
                    >
                      <svg
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="flex-1 relative">
                  <button
                    type="button"
                    class="w-full h-[42px] px-4 py-2 text-left border border-slate-200 dark:border-n-weak rounded-lg bg-white dark:bg-n-solid-1 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 flex items-center justify-between"
                    @click="isLabelDropdownOpen = !isLabelDropdownOpen"
                  >
                    <span
                      :class="
                        selectedLabels.length === 0
                          ? 'text-slate-400 dark:text-n-slate-10'
                          : 'text-slate-900 dark:text-n-slate-12'
                      "
                    >
                      {{ selectedLabelsText }}
                    </span>
                    <svg
                      class="w-5 h-5 text-slate-400 dark:text-n-slate-10"
                      :class="{ 'transform rotate-180': isLabelDropdownOpen }"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <div
                    v-if="isLabelDropdownOpen"
                    class="absolute z-50 w-full mt-1 bg-white dark:bg-n-solid-1 border border-slate-200 dark:border-n-weak rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    <div
                      v-if="labelOptions.length === 0"
                      class="px-4 py-3 text-sm text-slate-500 dark:text-n-slate-11"
                    >
                      {{
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.NO_LABELS'
                        )
                      }}
                    </div>
                    <label
                      v-for="option in labelOptions"
                      :key="option.value"
                      class="flex items-center px-4 py-2 hover:bg-slate-50 dark:hover:bg-n-solid-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :checked="isLabelSelected(option.value)"
                        class="w-4 h-4 text-woot-500 border-slate-300 dark:border-n-weak rounded focus:ring-woot-500"
                        @change="toggleLabel(option.value)"
                      />
                      <span
                        class="ml-3 text-sm text-slate-900 dark:text-n-slate-12"
                      >
                        {{ option.label }}
                      </span>
                      <span
                        v-if="option.color"
                        class="ml-auto w-3 h-3 rounded-full"
                        :style="{ backgroundColor: option.color }"
                      />
                    </label>

                    <div
                      v-if="selectedLabels.length > 0"
                      class="border-t border-slate-200 dark:border-n-weak px-4 py-2 flex justify-between items-center bg-slate-50 dark:bg-n-solid-2"
                    >
                      <span class="text-xs text-slate-600 dark:text-n-slate-11">
                        {{
                          $t(
                            'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.LABELS_SELECTED',
                            { count: selectedLabels.length }
                          )
                        }}
                      </span>
                      <button
                        type="button"
                        class="text-xs text-woot-500 hover:text-woot-600 font-medium"
                        @click="selectedLabels = []"
                      >
                        {{
                          $t(
                            'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CLEAR_FILTERS'
                          )
                        }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="mt-4 pt-4 border-t border-slate-200 dark:border-n-weak"
              >
                <div
                  class="text-sm font-medium text-slate-900 dark:text-n-slate-12 mb-3"
                >
                  {{
                    $t(
                      'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.TITLE'
                    )
                  }}
                </div>
                <div class="grid grid-cols-4 gap-3 items-start">
                  <div>
                    <label
                      class="block text-xs font-medium text-slate-700 dark:text-n-slate-11 mb-1"
                    >
                      {{
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.FIELD'
                        )
                      }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="customAttributeDraft.attributeKey"
                        class="w-full h-[38px] rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-3 pr-9 text-sm appearance-none"
                        @change="onCustomAttributeKeyChange"
                      >
                        <option value="" disabled>
                          {{
                            $t(
                              'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.FIELD_PLACEHOLDER'
                            )
                          }}
                        </option>
                        <option
                          v-for="option in customAttributeOptions"
                          :key="option.key"
                          :value="option.key"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <svg
                        class="w-5 h-5 text-slate-400 dark:text-n-slate-10 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label
                      class="block text-xs font-medium text-slate-700 dark:text-n-slate-11 mb-1"
                    >
                      {{
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.OPERATOR'
                        )
                      }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="customAttributeDraft.operator"
                        class="w-full h-[38px] rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-3 pr-9 text-sm appearance-none"
                      >
                        <option value="equals">
                          {{
                            $t(
                              'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.OPERATOR_EQUALS'
                            )
                          }}
                        </option>
                        <option value="not_equals">
                          {{
                            $t(
                              'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.OPERATOR_NOT_EQUALS'
                            )
                          }}
                        </option>
                      </select>
                      <svg
                        class="w-5 h-5 text-slate-400 dark:text-n-slate-10 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label
                      class="block text-xs font-medium text-slate-700 dark:text-n-slate-11 mb-1"
                    >
                      {{
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.VALUE'
                        )
                      }}
                    </label>

                    <div
                      v-if="selectedCustomAttributeInputType === 'list'"
                      class="relative"
                    >
                      <select
                        v-model="customAttributeDraft.value"
                        class="w-full h-[38px] rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-3 pr-9 text-sm appearance-none"
                      >
                        <option value="" disabled>
                          {{
                            $t(
                              'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.VALUE_PLACEHOLDER'
                            )
                          }}
                        </option>
                        <option
                          v-for="option in customAttributeValueOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <svg
                        class="w-5 h-5 text-slate-400 dark:text-n-slate-10 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    <div
                      v-else-if="
                        selectedCustomAttributeInputType === 'checkbox'
                      "
                      class="relative"
                    >
                      <select
                        v-model="customAttributeDraft.value"
                        class="w-full h-[38px] rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-3 pr-9 text-sm appearance-none"
                      >
                        <option value="" disabled>
                          {{
                            $t(
                              'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.VALUE_PLACEHOLDER'
                            )
                          }}
                        </option>
                        <option
                          v-for="option in customAttributeValueOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <svg
                        class="w-5 h-5 text-slate-400 dark:text-n-slate-10 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    <input
                      v-else-if="selectedCustomAttributeInputType === 'date'"
                      v-model="customAttributeDraft.value"
                      type="date"
                      class="w-full h-[38px] rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-3 text-sm"
                    />

                    <input
                      v-else
                      v-model="customAttributeDraft.value"
                      type="text"
                      :placeholder="
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.VALUE_PLACEHOLDER'
                        )
                      "
                      class="w-full h-[38px] rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-3 text-sm"
                    />
                  </div>

                  <div>
                    <div class="h-4 mb-1" aria-hidden="true" />
                    <Button
                      color="blue"
                      class="!flex !h-[38px] w-full justify-center"
                      :disabled="
                        !customAttributeDraft.attributeKey ||
                        !hasCustomAttributeDraftValue
                      "
                      @click="addCustomAttributeFilter"
                    >
                      {{
                        $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.CUSTOM_ATTRIBUTES.ADD'
                        )
                      }}
                    </Button>
                  </div>
                </div>

                <div
                  v-if="selectedCustomAttributeFilters.length > 0"
                  class="mt-3 flex flex-wrap gap-2"
                >
                  <div
                    v-for="(filter, index) in selectedCustomAttributeFilters"
                    :key="`${filter.name}-${index}`"
                    class="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1"
                  >
                    <span class="text-xs text-slate-700 dark:text-n-slate-11">
                      {{ filter.label }}
                      {{ getCustomAttributeOperatorLabel(filter.operator) }}
                      <strong>{{ filter.value }}</strong>
                    </span>
                    <button
                      class="text-slate-400 hover:text-slate-700 dark:hover:text-n-slate-12"
                      @click="removeCustomAttributeFilter(index)"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="mt-4 flex justify-end">
                  <Button color="blue" @click="applyFilters">
                    {{ $t('CAMPAIGN.WHATSAPP.APPLY_FILTERS') }}
                  </Button>
                </div>
              </div>
            </div>

            <div
              class="flex items-center justify-between mb-4 pb-3 border-b border-slate-200 dark:border-n-weak"
            >
              <Button
                color="slate"
                :disabled="isLoadingContacts"
                @click="selectAllFromFilter"
              >
                {{
                  isLoadingContacts
                    ? $t(
                        'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.SELECTING'
                      )
                    : $t(
                        'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.SELECT_ALL',
                        { count: totalContactsCount }
                      )
                }}
              </Button>
            </div>

            <div
              v-if="isLoadingContacts"
              class="text-center py-8 text-slate-500 dark:text-n-slate-11"
            >
              {{
                $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.LOADING')
              }}
            </div>
            <div
              v-else-if="visibleContacts.length === 0"
              class="text-center py-8 text-slate-500 dark:text-n-slate-11"
            >
              {{
                $t(
                  'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.NO_CONTACTS'
                )
              }}
            </div>
            <div v-else class="space-y-2 max-h-96 overflow-y-auto">
              <div
                v-for="contact in visibleContacts"
                :key="contact.id"
                class="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-n-solid-2 transition-colors cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-n-weak"
                :class="{
                  'bg-woot-25 dark:bg-woot-800/10 !border-woot-75 dark:!border-woot-700/30':
                    isContactSelected(contact.id),
                }"
                @mouseenter="hoveredContactId = contact.id"
                @mouseleave="hoveredContactId = null"
                @click="
                  onContactCheckboxChange(
                    contact.id,
                    !isContactSelected(contact.id)
                  )
                "
              >
                <div class="flex-shrink-0">
                  <label
                    class="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer"
                    @click.stop
                  >
                    <input
                      type="checkbox"
                      class="w-4 h-4 cursor-pointer"
                      :checked="isContactSelected(contact.id)"
                      @change="
                        onContactCheckboxChange(
                          contact.id,
                          $event.target.checked
                        )
                      "
                    />
                  </label>
                </div>
                <div class="flex-1 min-w-0">
                  <div
                    class="font-medium text-slate-900 dark:text-n-slate-12 truncate"
                  >
                    {{
                      contact.name ||
                      $t(
                        'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.NO_NAME'
                      )
                    }}
                  </div>
                  <div
                    class="text-sm text-slate-500 dark:text-n-slate-11 truncate"
                  >
                    {{
                      contact.email ||
                      contact.phone_number ||
                      $t(
                        'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTACTS_TAB.NO_CONTACT_INFO'
                      )
                    }}
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!isLoadingContacts && visibleContacts.length > 0"
              class="mt-6"
            >
              <PaginationFooter
                :current-page="currentPage"
                :total-items="totalContactsCount"
                :items-per-page="itemsPerPage"
                @update:current-page="changePage"
              />
            </div>

            <div class="flex justify-end gap-8 mt-8">
              <template v-if="!isViewMode">
                <Button color="ruby" variant="link" @click="onCancel">
                  {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.CANCEL') }}
                </Button>
                <Button
                  color="blue"
                  variant="link"
                  :disabled="isSaving"
                  @click="onSaveExit"
                >
                  {{
                    isSaving
                      ? $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SAVING')
                      : $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SAVE_EXIT'
                        )
                  }}
                </Button>
              </template>
              <Button
                color="slate"
                :disabled="!hasAudienceSelection"
                @click="onContinue"
              >
                {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.CONTINUE') }}
              </Button>
            </div>
          </div>
          <div v-else-if="activeTab === 1">
            <div class="font-bold mb-1 text-slate-900 dark:text-n-slate-12">
              {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTENT_TAB.TITLE') }}
            </div>
            <div class="text-slate-500 dark:text-n-slate-11 mb-6">
              {{
                $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTENT_TAB.SUBTITLE')
              }}
            </div>
            <div>
              <label
                class="block text-sm font-medium text-slate-900 dark:text-n-slate-12 mb-1"
              >
                {{
                  $t(
                    'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTENT_TAB.SELECT_TEMPLATE_LABEL'
                  )
                }}
                <span class="text-n-ruby-9">&ast;</span>
              </label>
              <div class="flex items-center gap-3">
                <div class="w-96">
                  <ComboBox
                    v-model="selectedTemplate"
                    :options="templateOptions"
                    :placeholder="
                      $t(
                        'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.CONTENT_TAB.SELECT_TEMPLATE_PLACEHOLDER'
                      )
                    "
                  />
                </div>
                <Button
                  :disabled="isSyncingTemplates || !selectedWhatsappAccount"
                  @click="syncTemplates"
                >
                  {{
                    isSyncingTemplates
                      ? $t(
                          'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNCING'
                        )
                      : $t(
                          'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON'
                        )
                  }}
                </Button>
              </div>
            </div>

            <WhatsAppTemplateParser
              v-if="currentTemplate"
              :key="templateParserKey"
              ref="templateParserRef"
              :template="currentTemplate"
              :inbox-id="Number(selectedWhatsappAccount)"
              :initial-params="templateInitialParams"
              class="mt-6"
            />

            <div class="flex justify-end gap-8 mt-8">
              <template v-if="!isViewMode">
                <Button color="ruby" variant="link" @click="onCancel">
                  {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.CANCEL') }}
                </Button>
                <Button
                  color="blue"
                  variant="link"
                  :disabled="isSaving"
                  @click="onSaveExit"
                >
                  {{
                    isSaving
                      ? $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SAVING')
                      : $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SAVE_EXIT'
                        )
                  }}
                </Button>
              </template>
              <Button
                color="blue"
                :disabled="!selectedTemplate || !isTemplateFormValid"
                @click="onContinue"
              >
                {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.CONTINUE') }}
              </Button>
            </div>
          </div>
          <div v-else-if="activeTab === 2">
            <div class="font-bold mb-1 text-slate-900 dark:text-n-slate-12">
              {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.SEND_TAB.TITLE') }}
            </div>
            <div class="text-slate-500 dark:text-n-slate-11 mb-6">
              {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.SEND_TAB.SUBTITLE') }}
            </div>
            <div class="flex gap-8 items-end mb-8">
              <div>
                <label
                  class="block text-sm font-medium text-slate-900 dark:text-n-slate-12 mb-1"
                >
                  {{
                    $t(
                      'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.SEND_TAB.SEND_DATE_LABEL'
                    )
                  }}
                  <span class="text-n-ruby-9">&ast;</span>
                </label>
                <input
                  v-model="sendDate"
                  type="date"
                  class="w-40 rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                />
              </div>
              <div>
                <label
                  class="block text-sm font-medium text-slate-900 dark:text-n-slate-12 mb-1"
                >
                  {{
                    $t(
                      'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.SEND_TAB.TIME_LABEL'
                    )
                  }}
                  <span class="text-n-ruby-9">&ast;</span>
                </label>
                <div class="flex items-center gap-2">
                  <select
                    v-model="sendHour"
                    class="w-16 rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/30"
                  >
                    <option
                      v-for="h in 24"
                      :key="h"
                      :value="String(h).padStart(2, '0')"
                    >
                      {{ String(h).padStart(2, '0') }}
                    </option>
                  </select>
                  <span class="text-slate-400 dark:text-n-slate-10 text-lg">
                    &colon;
                  </span>
                  <select
                    v-model="sendMinute"
                    class="w-16 rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/30"
                  >
                    <option
                      v-for="m in 60"
                      :key="m"
                      :value="String(m - 1).padStart(2, '0')"
                    >
                      {{ String(m - 1).padStart(2, '0') }}
                    </option>
                  </select>
                </div>
              </div>
              <div>
                <label
                  class="block text-sm font-medium text-slate-900 dark:text-n-slate-12 mb-1"
                >
                  {{
                    $t(
                      'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.SEND_TAB.TIMEZONE_LABEL'
                    )
                  }}
                  <span class="text-n-ruby-9">&ast;</span>
                </label>
                <select
                  v-model="sendTimezone"
                  class="w-64 rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/30"
                >
                  <option value="America/Sao_Paulo">
                    {{
                      $t(
                        'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.SEND_TAB.TIMEZONE_BRASILIA'
                      )
                    }}
                  </option>
                </select>
              </div>
            </div>
            <div class="flex justify-end gap-8 mt-8">
              <Button color="ruby" variant="link" @click="onCancel">
                {{ $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.CANCEL') }}
              </Button>
              <template v-if="!isViewMode">
                <Button
                  color="blue"
                  variant="link"
                  :disabled="isSaving"
                  @click="onSaveExit"
                >
                  {{
                    isSaving
                      ? $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SAVING')
                      : $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SAVE_EXIT'
                        )
                  }}
                </Button>
                <Button
                  color="blue"
                  class="flex items-center gap-2"
                  :disabled="isSaving"
                  @click="onSendNow"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M5 12h14M12 5l7 7-7 7"
                    />
                  </svg>
                  {{
                    isSaving
                      ? $t(
                          'CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SENDING'
                        )
                      : $t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN_PAGE.BUTTONS.SEND')
                  }}
                </Button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
